/**
 * Flow Diagram Component
 * Implements PRD FR-35 - Trade flow visualization
 */

import { useSimulationStore } from '../state/simulationStore';

export function FlowDiagram() {
  const { simulation } = useSimulationStore();

  if (!simulation) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          Run a simulation to see trade flows
        </div>
      </div>
    );
  }

  const { currentTime, params, equilibrium } = simulation;
  const { n, i } = params;
  const { S1, S2, q1_MM, q1_LT1 } = equilibrium;

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Trade Flow at t = {currentTime}
      </h3>

      <svg viewBox="0 0 400 200" className="w-full max-w-lg mx-auto">
        {currentTime === 1 && (
          <>
            {/* LT1 Box */}
            <rect x="20" y="70" width="80" height="60" rx="8" fill="#fed7aa" stroke="#f97316" strokeWidth="2" />
            <text x="60" y="95" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#c2410c">LT1</text>
            <text x="60" y="115" textAnchor="middle" fontSize="10" fill="#ea580c">Holds: {i.toFixed(2)}</text>

            {/* MMs Box */}
            <rect x="280" y="70" width="100" height="60" rx="8" fill="#ede9fe" stroke="#8b5cf6" strokeWidth="2" />
            <text x="330" y="95" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#6d28d9">MMs (n={n})</text>
            <text x="330" y="115" textAnchor="middle" fontSize="10" fill="#7c3aed">Buy: {(q1_MM * n).toFixed(2)}</text>

            {/* Arrow LT1 -> MMs */}
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
              </marker>
            </defs>
            <line x1="100" y1="100" x2="270" y2="100" stroke="#3b82f6" strokeWidth="4" markerEnd="url(#arrowhead)" />

            {/* Flow labels */}
            <text x="185" y="85" textAnchor="middle" fontSize="11" fill="#1e40af" fontWeight="bold">
              {(n * i / (n + 1)).toFixed(2)} units
            </text>
            <text x="185" y="130" textAnchor="middle" fontSize="10" fill="#3b82f6">
              @ S₁ = {S1.toFixed(2)}
            </text>

            {/* Partial liquidation note */}
            <text x="200" y="170" textAnchor="middle" fontSize="10" fill="#6b7280">
              LT1 retains: {q1_LT1.toFixed(2)} units (i/(n+1))
            </text>
          </>
        )}

        {currentTime === 2 && (
          <>
            {/* MMs Box */}
            <rect x="20" y="30" width="80" height="50" rx="8" fill="#ede9fe" stroke="#8b5cf6" strokeWidth="2" />
            <text x="60" y="50" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#6d28d9">MMs</text>
            <text x="60" y="68" textAnchor="middle" fontSize="9" fill="#7c3aed">Sell: {(q1_MM * n).toFixed(2)}</text>

            {/* LT1 Box */}
            <rect x="20" y="120" width="80" height="50" rx="8" fill="#fed7aa" stroke="#f97316" strokeWidth="2" />
            <text x="60" y="140" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#c2410c">LT1</text>
            <text x="60" y="158" textAnchor="middle" fontSize="9" fill="#ea580c">Sell: {q1_LT1.toFixed(2)}</text>

            {/* LT2 Box */}
            <rect x="280" y="70" width="100" height="60" rx="8" fill="#cffafe" stroke="#06b6d4" strokeWidth="2" />
            <text x="330" y="95" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#0e7490">LT2</text>
            <text x="330" y="115" textAnchor="middle" fontSize="10" fill="#0891b2">Buys: {i.toFixed(2)}</text>

            {/* Arrows */}
            <defs>
              <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#22c55e" />
              </marker>
            </defs>

            {/* MMs -> LT2 */}
            <line x1="100" y1="55" x2="270" y2="90" stroke="#22c55e" strokeWidth="3" markerEnd="url(#arrowhead2)" />

            {/* LT1 -> LT2 */}
            <line x1="100" y1="145" x2="270" y2="110" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrowhead2)" />

            {/* Price label */}
            <text x="185" y="50" textAnchor="middle" fontSize="10" fill="#16a34a">
              @ S₂ = {S2.toFixed(2)}
            </text>

            {/* Note */}
            <text x="200" y="185" textAnchor="middle" fontSize="10" fill="#6b7280">
              All inventories return to zero
            </text>
          </>
        )}

        {currentTime === 3 && (
          <>
            {/* Information Box */}
            <rect x="100" y="60" width="200" height="80" rx="8" fill="#f0fdf4" stroke="#22c55e" strokeWidth="2" />
            <text x="200" y="90" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#166534">
              Terminal Value Realized
            </text>
            <text x="200" y="115" textAnchor="middle" fontSize="12" fill="#15803d">
              S₃ = {equilibrium.S3.toFixed(4)}
            </text>
            <text x="200" y="135" textAnchor="middle" fontSize="10" fill="#6b7280">
              No trading at t=3
            </text>
          </>
        )}
      </svg>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-orange-300 rounded" />
          <span>LT1</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-purple-200 rounded" />
          <span>Market Makers</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-cyan-200 rounded" />
          <span>LT2</span>
        </div>
      </div>
    </div>
  );
}
